import { useRef, useEffect, useMemo } from 'react';
import { createError } from '@adv-frontend/error';
import { deepEqual } from '@direct-frontend/stdlib';
import type { Reaction } from '../createReaction/createReaction';
import { reportError } from '../utils';

export const UnstableReactionError = createError('UnstableReactionError');

/**
 * Выбрасывает ошибку если изменилась ссылка на реакцию.
 *
 * Проверка необходима, так как использование нестабильной ссылки может привести к неожиданному результату
 *
 * Например: `useReaction(takeLatest(someReaction))` будет работать как takeEvery вместо takeLatest.
 *
 * @param currentReaction Текущая используемая реакция
 * @param passedReaction Переданная реакция
 */
function assertReactionStable(currentReaction: unknown, passedReaction: unknown) {
    if (currentReaction === passedReaction) {
        return;
    }

    const error = new UnstableReactionError('ссылка на реакцию изменилась', { cause: null });

    reportError(error);
}

/**
 * Хук для подключения реакции к React компоненту.
 *
 * @param reaction реакция
 * @param param параметр реакции
 */
export function useReaction<R>(reaction: Reaction<R, never>): void;
export function useReaction<P, R>(reaction: Reaction<R, P>, param: P): void;
export function useReaction<P, R>(reaction: Reaction<R, never> | Reaction<R, P>, param?: P): void {
    // Храним ссылку на изначально переданную реакцию.
    const initialPassedReactionRef = useRef(reaction);
    // Проверяем что ссылка не изменилась.
    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    assertReactionStable(initialPassedReactionRef.current, reaction);

    // Храним ссылку на предыдущий параметр реакции
    const cachedParamRef = useRef(param);

    // мемоизированный по deepEqual параметр реакции
    const memoizedParam = useMemo(() => {
        // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
        if (deepEqual(param, cachedParamRef.current)) {
            // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
            return cachedParamRef.current;
        }

        // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
        cachedParamRef.current = param;
        return param;
    }, [param]);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
        // @ts-expect-error -- если у реакции нет параметра, то в param будет undefined и это ок
        const detach = reaction.start(memoizedParam);

        return detach;
    }, [reaction, memoizedParam]);
}
